import { useEffect, useState } from "react";
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, clearSession, currentUser, downloadFile } from "@/lib/api";
import {
  LayoutDashboard, Calendar as CalIcon, ListChecks, Users, UserPlus, MessageSquareWarning,
  CreditCard, Wallet, BarChart3, Settings as SettingsIcon, Bot, LogOut, ClipboardList, TrendingUp, Building2, ShieldCheck, Plug, Clock, Landmark, FileSignature, IndianRupee, UserCog, Image as ImageIcon, Eraser, Menu as MenuIcon, X as XIcon
} from "lucide-react";
import AdminCalendar from "./Calendar";
import Forecast from "./Forecast";
import SecurityPage from "./SecurityPage";
import IntegrationsPage from "./IntegrationsPage";
import BookingRequests from "./BookingRequests";
import CompanyBranding from "./CompanyBranding";
import BookingManagement from "./control/BookingManagement";
import PaymentManagement from "./control/PaymentManagement";
import PaymentGateway from "./control/PaymentGateway";
import AgreementSigning from "./control/AgreementSigning";
import PricingPage from "./control/PricingPage";
import AdminUsers from "./control/AdminUsers";
import DscExpiryBanner from "./control/DscExpiryBanner";
import AuditLogs from "./control/AuditLogs";
import SigningReminder from "./control/SigningReminder";
import TestData from "./control/TestData";
import { RevenueChart } from "./control/RevenueChart";

const SIDE = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/calendar", label: "Calendar", icon: CalIcon },
  { to: "/admin/booking-requests", label: "Booking Requests", icon: Clock },
  { to: "/admin/bookings", label: "Booking Management", icon: ListChecks },
  { to: "/admin/company", label: "Company & Branding", icon: Building2, sa: true },
  { to: "/admin/company?tab=images", label: "Website Images", icon: ImageIcon, sa: true },
  { to: "/admin/test-data", label: "Mock & Test Data", icon: Eraser, sa: true },
  { to: "/admin/forecast", label: "Demand Forecast", icon: TrendingUp },
  { to: "/admin/franchises", label: "Franchises", icon: Building2 },
  { to: "/admin/settlements", label: "Franchise Settlements", icon: Wallet },
  { to: "/admin/agreement-signing", label: "Agreement Signing", icon: FileSignature, sa: true },
  { to: "/admin/workers", label: "Workers", icon: Users },
  { to: "/admin/applications", label: "Applications", icon: UserPlus },
  { to: "/admin/complaints", label: "Complaints", icon: MessageSquareWarning },
  { to: "/admin/payments", label: "Payment Management", icon: CreditCard },
  { to: "/admin/payment-gateway", label: "Payment Gateway", icon: Landmark, sa: true },
  { to: "/admin/payouts", label: "Payouts", icon: Wallet },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/copilot", label: "AI Copilot", icon: Bot },
  { to: "/admin/audit", label: "Audit Log", icon: ClipboardList, sa: true },
  { to: "/admin/pricing", label: "Pricing & Fees", icon: IndianRupee, sa: true },
  { to: "/admin/admin-users", label: "Admin Users", icon: UserCog, sa: true },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, sa: true },
  { to: "/admin/integrations", label: "Integrations", icon: Plug, sa: true },
  { to: "/admin/security", label: "Security", icon: ShieldCheck, sa: true },
];

export default function AdminApp() {
  const nav = useNavigate();
  const u = currentUser();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  if (!u || !["admin", "super_admin"].includes(localStorage.getItem("karmivo_role"))) return <Navigate to="/admin/login"/>;
  const isSA = localStorage.getItem("karmivo_role") === "super_admin";
  const items = SIDE.filter((s) => isSA || !s.sa);
  const SA = (el) => (isSA ? el : <Navigate to="/admin/dashboard"/>);

  const sidebar = (
    <>
      <div className="flex items-center gap-2 mb-6 px-2">
        <div className="w-9 h-9 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold">K</div>
        <div>
          <div className="font-display font-extrabold leading-tight">KARMIVO</div>
          <div className="text-[10px] uppercase tracking-widest text-orange-400">Operations</div>
        </div>
      </div>
      <nav className="space-y-1">
        {items.map(s => {
          const active = (loc.pathname + loc.search) === s.to || (loc.pathname === s.to && !(s.to === "/admin/company" && loc.search.includes("tab=images")));
          const Icon = s.icon;
          return (
            <Link key={s.to} to={s.to} data-testid={`side-${s.label.toLowerCase().replace(/ /g,'-')}`}
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm ${active ? "bg-orange-500/20 text-orange-300 font-semibold" : "text-slate-400 hover:bg-slate-800 hover:text-slate-100"}`}>
              <Icon size={16}/>{s.label}
            </Link>
          );
        })}
      </nav>
      <Button variant="ghost" size="sm" className="w-full mt-6 text-slate-400" onClick={() => { clearSession(); nav("/"); }} data-testid="admin-logout">
        <LogOut size={14} className="mr-2"/>Logout
      </Button>
    </>
  );

  return (
    <div className="admin-shell flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:block w-60 shrink-0 bg-slate-950 border-r border-slate-800 min-h-screen p-4 sticky top-0 self-start">
        {sidebar}
      </aside>
      {/* Mobile top bar + drawer */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-slate-950 border-b border-slate-800 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-orange-500 rounded-md flex items-center justify-center text-white font-bold text-xs">K</div>
          <span className="font-display font-extrabold text-sm">KARMIVO</span>
        </div>
        <button onClick={() => setMenuOpen(!menuOpen)} className="text-slate-300 p-2" data-testid="admin-menu-toggle" aria-label="Menu">
          {menuOpen ? <XIcon size={20}/> : <MenuIcon size={20}/>}
        </button>
      </div>
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/60" onClick={() => setMenuOpen(false)}>
          <aside className="w-64 bg-slate-950 min-h-screen p-4 pt-16" onClick={(e) => e.stopPropagation()}>
            {sidebar}
          </aside>
        </div>
      )}
      <main className="flex-1 p-4 md:p-6 min-w-0 pt-20 md:pt-6 overflow-x-hidden">
        <Routes>
          <Route path="/dashboard" element={<Dashboard/>}/>
          <Route path="/calendar" element={<AdminCalendar/>}/>
          <Route path="/forecast" element={<Forecast/>}/>
          <Route path="/franchises" element={<Franchises/>}/>
          <Route path="/settlements" element={<Settlements/>}/>
          <Route path="/agreement-signing" element={SA(<AgreementSigning/>)}/>
          <Route path="/bookings" element={<BookingManagement/>}/>
          <Route path="/booking-requests" element={<BookingRequests/>}/>
          <Route path="/company" element={SA(<CompanyBranding/>)}/>
          <Route path="/workers" element={<Workers kyc="approved"/>}/>
          <Route path="/applications" element={<Workers kyc="submitted"/>}/>
          <Route path="/complaints" element={<Complaints/>}/>
          <Route path="/payments" element={<PaymentManagement/>}/>
          <Route path="/payment-gateway" element={SA(<PaymentGateway/>)}/>
          <Route path="/payouts" element={<Payouts/>}/>
          <Route path="/reports" element={<Reports/>}/>
          <Route path="/copilot" element={<Copilot/>}/>
          <Route path="/audit" element={SA(<AuditLogs/>)}/>
          <Route path="/settings" element={SA(<SettingsPage/>)}/>
          <Route path="/pricing" element={SA(<PricingPage/>)}/>
          <Route path="/admin-users" element={SA(<AdminUsers/>)}/>
          <Route path="/test-data" element={SA(<TestData/>)}/>
          <Route path="/integrations" element={SA(<IntegrationsPage/>)}/>
          <Route path="/security" element={SA(<SecurityPage/>)}/>
          <Route path="*" element={<Navigate to="/admin/dashboard"/>}/>
        </Routes>
      </main>
    </div>
  );
}

const KPI = ({ label, value, color = "text-orange-400", testid }) => (
  <Card className="admin-card p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
    <div className={`font-mono text-2xl font-bold mt-1 ${color}`}>{value}</div>
  </Card>
);

function Dashboard() {
  const [d, setD] = useState(null);
  const [integ, setInteg] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then(r => setD(r.data)).catch(e => toast.error(e.response?.data?.detail || "load failed"));
    api.get("/admin/integrations/status").then(r => setInteg(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="text-slate-400">Loading…</div>;
  const Pill = ({ label }) => {
    const live = label === "Live";
    return <span className={`px-2 py-0.5 rounded text-xs font-bold ${live ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{label}</span>;
  };
  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-bold mb-5">Operations Dashboard</h1>
      <DscExpiryBanner/>
      <SigningReminder/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Today's Bookings" value={d.todays_bookings} testid="kpi-today"/>
        <KPI label="Pending Payment" value={d.pending_payment} color="text-amber-400" testid="kpi-pending"/>
        <KPI label="In Progress" value={d.in_progress} color="text-blue-400" testid="kpi-progress"/>
        <KPI label="Completed" value={d.completed} color="text-emerald-400" testid="kpi-completed"/>
        <KPI label="Partially Assigned" value={d.partially_assigned} color="text-blue-400"/>
        <KPI label="Fully Assigned" value={d.fully_assigned} color="text-emerald-400"/>
        <KPI label="Available Workers" value={d.available_workers} color="text-emerald-400" testid="kpi-available"/>
        <KPI label="Busy Workers" value={d.busy_workers} color="text-amber-400"/>
        <KPI label="Pending KYC" value={d.pending_applications} color="text-amber-400"/>
        <KPI label="Complaints Open" value={d.complaints_open} color="text-red-400"/>
        <KPI label="Revenue ₹" value={d.revenue} color="text-orange-400" testid="kpi-revenue"/>
        <KPI label="Payout Liability ₹" value={d.payout_liability} color="text-slate-200"/>
      </div>
      <RevenueChart/>
      {integ && (
        <Card className="admin-card p-4 mt-5" data-testid="integrations-status">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Integrations</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center justify-between" data-testid="integ-payments"><span className="text-slate-400">Payment Gateway (Razorpay)</span><Pill label={integ.payments.status_label}/></div>
            <div className="flex items-center justify-between" data-testid="integ-sms"><span className="text-slate-400">SMS Provider (MSG91)</span><Pill label={integ.sms.status_label}/></div>
            <div className="flex items-center justify-between" data-testid="integ-ai"><span className="text-slate-400">AI (OpenAI via Emergent)</span><Pill label={integ.ai.status_label}/></div>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            {localStorage.getItem("karmivo_role") === "super_admin" ? <>Connect Razorpay and switch on online payments from <Link to="/admin/payment-gateway" className="text-orange-400 hover:underline" data-testid="dash-gateway-link">Payment Gateway</Link>. SMS keys live under Integrations.</> : "Online payments and integrations are managed by the Super Admin."}
          </p>
        </Card>
      )}
    </div>
  );
}

function Workers({ kyc }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/admin/workers`, { params: kyc ? { kyc_status: kyc } : {} }).then(r => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kyc]);
  const setStatus = async (id, status) => { await api.post(`/admin/workers/${id}/kyc`, null, { params: { status } }); toast.success(status); load(); };
  return (
    <div data-testid={`admin-workers-${kyc}`}>
      <h1 className="font-display text-3xl font-bold mb-5">{kyc === "submitted" ? "Worker Applications" : "Workers"}</h1>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map(w => (
          <Card key={w.id} className="admin-card p-4" data-testid={`worker-${w.phone}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-bold">{w.name}</div>
                <div className="text-xs text-slate-400">{w.phone} • {w.category}</div>
                <div className="text-xs text-slate-400">Skills: {(w.skills||[]).join(", ")} • Exp {w.experience_years}y</div>
                <div className="text-xs text-slate-400">Rating {w.rating_avg} • Jobs {w.completed_jobs}</div>
              </div>
              <span className={`text-[10px] font-bold px-2 py-1 rounded ${w.kyc_status === "approved" ? "bg-emerald-500/20 text-emerald-300" : w.kyc_status === "rejected" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>{w.kyc_status}</span>
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              {w.kyc_status !== "approved" && <Button size="sm" className="bg-emerald-600" onClick={() => setStatus(w.id, "approved")} data-testid={`approve-${w.phone}`}>Approve</Button>}
              {w.kyc_status !== "rejected" && <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => setStatus(w.id, "rejected")} data-testid={`reject-${w.phone}`}>Reject</Button>}
              {w.kyc_status !== "suspended" && <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => setStatus(w.id, "suspended")}>Suspend</Button>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Complaints() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/complaints").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, s) => { await api.post(`/admin/complaints/${id}/status`, null, { params: { status: s } }); load(); };
  return (
    <div data-testid="admin-complaints">
      <h1 className="font-display text-3xl font-bold mb-5">Complaints</h1>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-slate-400">No complaints.</div>}
        {items.map(c => (
          <Card key={c.id} className="admin-card p-4" data-testid={`complaint-${c.code}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono font-bold text-orange-300">{c.code}</div>
                <div className="text-sm">{c.description}</div>
                <div className="text-xs text-slate-500">Category: {c.category} • Priority: <span className={`font-bold ${c.priority === "high" || c.priority === "critical" ? "text-red-400" : "text-amber-400"}`}>{c.priority}</span></div>
              </div>
              <div className="flex gap-1">
                {["under_review", "in_progress", "resolved", "closed"].map(s => (
                  <Button key={s} size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => setStatus(c.id, s)}>{s}</Button>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Payouts() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/payouts").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => { await api.post(`/admin/payouts/${id}/approve`); toast.success("Paid"); load(); };
  return (
    <div data-testid="admin-payouts">
      <h1 className="font-display text-3xl font-bold mb-5">Worker Payouts</h1>
      <div className="space-y-2">
        {items.map(p => (
          <Card key={p.id} className="admin-card p-3 flex items-center justify-between" data-testid={`payout-${p.id}`}>
            <div><div className="font-mono text-xs text-slate-400">{p.worker_id?.slice(0,8)} / {p.booking_id?.slice(0,8)}</div><div className="font-bold text-emerald-400">₹{p.net}</div></div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded ${p.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{p.status}</span>
              {p.status !== "paid" && <Button size="sm" className="bg-emerald-600" onClick={() => approve(p.id)} data-testid={`approve-payout-${p.id}`}>Mark Paid</Button>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Reports() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/reports/summary").then(r => setD(r.data)); }, []);
  const exportCSV = () => {
    if (!d) return;
    const rows = Object.entries(d.service_demand).map(([k,v]) => `${k},${v}`).join("\n");
    const blob = new Blob([`service,count\n${rows}`], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "karmivo_reports.csv"; a.click();
  };
  if (!d) return <div className="text-slate-400">Loading…</div>;
  return (
    <div data-testid="admin-reports">
      <h1 className="font-display text-3xl font-bold mb-5">Reports</h1>
      <Button className="bg-orange-500 hover:bg-orange-600 mb-4" onClick={exportCSV} data-testid="export-csv-btn">Export CSV</Button>
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="admin-card p-5">
          <div className="font-bold mb-3">Service Demand</div>
          {Object.entries(d.service_demand).map(([k,v]) => (
            <div key={k} className="flex justify-between py-1 text-sm"><span>{k}</span><span className="font-mono font-bold">{v}</span></div>
          ))}
        </Card>
        <Card className="admin-card p-5">
          <div className="font-bold mb-3">Revenue by Service</div>
          {Object.entries(d.revenue_by_service || {}).map(([k,v]) => (
            <div key={k} className="flex justify-between py-1 text-sm"><span>{k}</span><span className="font-mono text-emerald-400 font-bold">₹{v}</span></div>
          ))}
          <div className="mt-3 pt-3 border-t border-slate-700 flex justify-between font-bold"><span>Total Revenue</span><span className="text-emerald-400">₹{d.total_revenue_inr}</span></div>
        </Card>
      </div>
    </div>
  );
}

function Copilot() {
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([{ role: "assistant", text: "Hi! Ask me about today's operations — bookings, revenue, workers, complaints, demand…" }]);
  const [loading, setLoading] = useState(false);
  const ask = async () => {
    if (!q.trim()) return;
    const myQ = q;
    setMessages(m => [...m, { role: "user", text: myQ }]); setQ(""); setLoading(true);
    try {
      const { data } = await api.post(`/admin/copilot`, { question: myQ });
      setMessages(m => [...m, { role: "assistant", text: data.answer }]);
    } catch (e) { setMessages(m => [...m, { role: "assistant", text: "Error: " + (e.response?.data?.detail || e.message) }]); }
    finally { setLoading(false); }
  };
  return (
    <div data-testid="admin-copilot">
      <h1 className="font-display text-3xl font-bold mb-2 flex items-center gap-2"><Bot className="text-orange-400"/>AI Operations Copilot</h1>
      <p className="text-slate-400 mb-4 text-sm">Powered by OpenAI via Emergent Universal Key. Answers from real database only.</p>
      <Card className="admin-card p-4 space-y-3 min-h-[400px]">
        {messages.map((m, i) => (
          <div key={i} className={`${m.role === "user" ? "text-right" : ""}`}>
            <div className={`inline-block max-w-[90%] px-4 py-2.5 rounded-lg text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-orange-500 text-white" : "bg-slate-800 text-slate-200"}`}>{m.text}</div>
          </div>
        ))}
        {loading && <div className="text-xs text-slate-500">Thinking…</div>}
      </Card>
      <div className="mt-3 flex gap-2">
        <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && ask()} placeholder="How many bookings pending today?" className="bg-slate-800 border-slate-700" data-testid="copilot-input"/>
        <Button className="bg-orange-500 hover:bg-orange-600" onClick={ask} disabled={loading} data-testid="copilot-send-btn">Ask</Button>
      </div>
    </div>
  );
}

function Franchises() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/franchises").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { try { await api.post(`/admin/franchises/${id}/status`, null, { params: { status } }); toast.success(status); load(); } catch (e) { toast.error(e.response?.data?.detail); } };
  return (
    <div data-testid="admin-franchises">
      <h1 className="font-display text-3xl font-bold mb-5">Franchise Partners</h1>
      <div className="grid md:grid-cols-2 gap-3">
        {items.length === 0 && <div className="text-slate-400 text-sm">No franchise applications yet.</div>}
        {items.map(f => (
          <Card key={f.id} className="admin-card p-4" data-testid={`franchise-${f.code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-bold flex items-center gap-2">{f.business_name} <span className="font-mono text-xs text-slate-500">{f.code}</span></div>
                <div className="text-xs text-slate-400">{f.applicant_name} · {f.phone} · {f.city}, {f.state} {f.pin_code}</div>
                <div className="text-xs text-slate-400 mt-1">Categories: {(f.territory?.service_categories || []).join(", ") || "—"}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${f.status === "active" || f.status === "approved" ? "bg-emerald-500/20 text-emerald-300" : f.status === "rejected" || f.status === "suspended" || f.status === "terminated" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>{f.status}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${f.fee_status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>Fee: {f.fee_status || "pending"}</span>
              </div>
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              {f.status !== "active" && f.status !== "approved" && f.fee_status === "paid" && <Button size="sm" className="bg-emerald-600" onClick={() => setStatus(f.id, "approved")} data-testid={`fr-approve-${f.code}`}>Approve</Button>}
              {f.status === "approved" && <Button size="sm" className="bg-blue-600" onClick={() => setStatus(f.id, "active")}>Activate</Button>}
              {f.status === "active" && <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300" onClick={() => setStatus(f.id, "suspended")}>Suspend</Button>}
              {f.status !== "rejected" && f.fee_status !== "paid" && <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => setStatus(f.id, "rejected")}>Reject</Button>}
              {f.fee_status !== "paid" && <span className="text-[10px] text-slate-500 self-center">Awaiting onboarding fee payment</span>}
              {f.fee_status === "paid" && <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" data-testid={`fr-agreement-${f.code}`}
                onClick={() => downloadFile(`/admin/franchises/${f.id}/agreement/pdf`, `Karmivo_Franchise_Agreement_${f.code}.pdf`).catch(() => toast.error("Download failed"))}>Agreement PDF</Button>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Settlements() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/settlements").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const run = async () => { try { const { data } = await api.post("/admin/settlements/run", null, { params: { frequency: "weekly" } }); toast.success(`Created ${data.created} settlement(s)`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const setStatus = async (id, status) => { await api.post(`/admin/settlements/${id}/status`, null, { params: { status } }); load(); };
  return (
    <div data-testid="admin-settlements">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-3xl font-bold">Franchise Settlements</h1>
        <Button className="bg-orange-500 hover:bg-orange-600" onClick={run} data-testid="run-settlements-btn">Run Settlement Batch</Button>
      </div>
      <Card className="admin-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800 text-slate-400 text-xs uppercase tracking-widest">
            <tr><th className="p-3 text-left">Code</th><th className="text-left">Franchise</th><th className="text-right">Count</th><th className="text-right">Gross</th><th className="text-right">Commission</th><th className="text-right">GST</th><th className="text-right">Net</th><th className="text-left">Status</th><th></th></tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id} className="border-t border-slate-800" data-testid={`settlement-${s.code}`}>
                <td className="p-3 font-mono">{s.code}</td>
                <td className="font-mono">{s.franchise_code}</td>
                <td className="text-right">{s.commission_count}</td>
                <td className="text-right">₹{s.gross_booking_value}</td>
                <td className="text-right">₹{s.commission}</td>
                <td className="text-right">₹{s.commission_gst}</td>
                <td className="text-right text-emerald-400 font-bold">₹{s.net_payable}</td>
                <td className="text-xs">{s.status}</td>
                <td className="pr-3">
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" data-testid={`pdf-settle-${s.code}`}
                      onClick={() => downloadFile(`/admin/settlements/${s.id}/pdf`, `Karmivo_Settlement_${s.code}.pdf`).catch(() => toast.error("Download failed"))}>PDF</Button>
                    {s.status !== "paid" && <Button size="sm" className="bg-emerald-600" onClick={() => setStatus(s.id, "paid")} data-testid={`pay-settle-${s.code}`}>Mark Paid</Button>}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="9" className="p-4 text-slate-500 text-sm text-center">No settlements yet. Click "Run Settlement Batch" to bundle unsettled commissions.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}



function SettingsPage() {
  const [s, setS] = useState(null);
  const [integ, setInteg] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then(r => setS(r.data));
    api.get("/admin/integrations/status").then(r => setInteg(r.data));
  }, []);
  if (!s) return null;
  const save = async () => { try { const { data } = await api.post("/admin/settings", s); setS(data); toast.success("Settings saved"); } catch (e) { toast.error(e.response?.data?.detail); } };
  const IntegRow = ({ title, data, keys }) => {
    const live = data?.status_label === "Live";
    return (
      <Card className="admin-card p-4" data-testid={`integ-row-${title.toLowerCase().replace(/ /g,'-')}`}>
        <div className="flex items-center justify-between">
          <div className="font-bold">{title}</div>
          <span className={`px-2.5 py-0.5 rounded text-xs font-bold ${live ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{data?.status_label || "Loading…"}</span>
        </div>
        <div className="text-xs text-slate-400 mt-1">Mode: <b>{data?.mode || "—"}</b> · Provider: <b>{data?.provider || "—"}</b></div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Required Env Variables</div>
          <div className="font-mono text-xs grid gap-1">
            {keys.map(k => {
              const present = (
                (k === "RAZORPAY_KEY_ID" && data?.key_id_present) ||
                (k === "RAZORPAY_KEY_SECRET" && data?.secret_present) ||
                (k === "RAZORPAY_WEBHOOK_SECRET" && data?.webhook_configured) ||
                (k === "MSG91_AUTH_KEY" && data?.auth_key_present) ||
                (k === "MSG91_TEMPLATE_ID" && data?.template_id_present) ||
                (k === "MSG91_SENDER_ID" && !!data?.sender_id)
              );
              return (
                <div key={k} className="flex justify-between items-center px-2 py-1 rounded bg-slate-900/60 border border-slate-800">
                  <span>{k}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${present ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{present ? "SET" : "EMPTY"}</span>
                </div>
              );
            })}
          </div>
        </div>
        {!live && (
          <p className="text-[11px] text-slate-500 mt-3">
            Paste the real values in <code className="text-slate-300">backend/.env</code> and restart the backend. Code is already production-ready — no further changes required to go live.
          </p>
        )}
      </Card>
    );
  };
  return (
    <div data-testid="admin-settings">
      <h1 className="font-display text-3xl font-bold mb-5">Settings</h1>

      <section data-testid="integrations-section" className="mb-6">
        <div className="text-sm font-bold mb-2 text-slate-300">Integrations</div>
        <div className="grid md:grid-cols-3 gap-3">
          <IntegRow title="Payment Gateway" data={integ?.payments} keys={["RAZORPAY_KEY_ID", "RAZORPAY_KEY_SECRET", "RAZORPAY_WEBHOOK_SECRET"]}/>
          <IntegRow title="SMS Provider" data={integ?.sms} keys={["MSG91_AUTH_KEY", "MSG91_TEMPLATE_ID", "MSG91_SENDER_ID"]}/>
          <Card className="admin-card p-4" data-testid="integ-row-ai">
            <div className="flex items-center justify-between">
              <div className="font-bold">AI Assistant</div>
              <span className="px-2.5 py-0.5 rounded text-xs font-bold bg-emerald-500/20 text-emerald-300">{integ?.ai?.status_label || "—"}</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">Model: <b>{integ?.ai?.model}</b></div>
            <div className="text-xs text-slate-500 mt-3">Uses Emergent Universal LLM Key — no extra setup required.</div>
          </Card>
        </div>
      </section>

      <Card className="admin-card p-5">
        <div className="font-bold mb-1">Operations</div>
        <p className="text-xs text-slate-500 mb-3">Service rates, platform fee, GST and cancellation charges are managed in <Link to="/admin/pricing" className="text-orange-400 hover:underline" data-testid="settings-pricing-link">Pricing & Fees</Link>.</p>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label className="text-slate-300 text-xs">Team Size</Label><Input type="number" value={s.team_size} onChange={e => setS(x => ({...x, team_size: parseInt(e.target.value || 0)}))} className="bg-slate-900 border-slate-700" data-testid="s-team"/></div>
          <div><Label className="text-slate-300 text-xs">Worker Payout %</Label><Input type="number" value={s.worker_payout_pct} onChange={e => setS(x => ({...x, worker_payout_pct: parseInt(e.target.value || 0)}))} className="bg-slate-900 border-slate-700" data-testid="s-payout"/></div>
          <div><Label className="text-slate-300 text-xs">Accept Timeout (min)</Label><Input type="number" value={s.acceptance_timeout_min} onChange={e => setS(x => ({...x, acceptance_timeout_min: parseInt(e.target.value || 0)}))} className="bg-slate-900 border-slate-700"/></div>
        </div>
      </Card>
      <Button className="bg-orange-500 hover:bg-orange-600 mt-4" onClick={save} data-testid="save-settings-btn">Save Settings</Button>
    </div>
  );
}
